/**
 * Schriftfarbe auf einer Markenflaeche.
 *
 * Helle Marken brauchen dunkle Schrift, dunkle Marken helle. Fest verdrahtete
 * Werte funktionieren nur, solange alle Kunden eine helle Marke fuehren:
 * Schwarz auf dem Rot von Minicar Kassel ergibt 3,33:1 und liegt damit unter
 * dem WCAG-Minimum von 4,5:1 — Weiss auf einem Marken-Gelb ist genauso falsch.
 *
 * Einzige Quelle fuer diese Entscheidung. `theme.js` leitet daraus
 * `palette.*.contrastText` ab; Komponenten, die eine Markenfarbe direkt als
 * Hintergrund setzen (und damit an MUIs contrastText vorbeilaufen), holen sich
 * die Schriftfarbe hier.
 */

export const relativeLuminance = (hex) => {
  const c = hex.replace('#', '');
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(c.substr(i, 2), 16) / 255);
  const f = (v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4);
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
};

export const contrastOn = (hex) => (relativeLuminance(hex) > 0.4 ? '#000000' : '#FFFFFF');

export const contrastRatio = (a, b) => {
  const [hi, lo] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
};

const toHex = (v) => Math.round(v).toString(16).padStart(2, '0');

// Lineare Mischung zweier Hex-Farben, t = Anteil von `b`.
export const mixHex = (a, b, t) => {
  const ca = a.replace('#', '');
  const cb = b.replace('#', '');
  const channel = (i) => {
    const va = parseInt(ca.substr(i, 2), 16);
    const vb = parseInt(cb.substr(i, 2), 16);
    return toHex(va + (vb - va) * t);
  };
  return `#${channel(0)}${channel(2)}${channel(4)}`.toUpperCase();
};

/**
 * Markenfarbe, die sich auf einem dunklen Hintergrund noch abhebt (Nachtmodus).
 * Helle Marken (Taxi-Gelb) bleiben unverändert; dunkle (Minicar-Rot, das fast
 * schwarze `secondary` aller Instanzen) werden schrittweise zu Weiß hin
 * aufgehellt, bis `minRatio` erreicht ist.
 */
export const readableOnDark = (hex, background, minRatio = 3) => {
  let t = 0;
  let color = hex;
  while (contrastRatio(color, background) < minRatio && t < 1) {
    t = Math.min(1, t + 0.05);
    color = mixHex(hex, '#FFFFFF', t);
  }
  return color;
};

export default contrastOn;
